import { countries } from '@/consts/languageAccordance';
import {
    ThemeVariables,
    variables as defaultVariables,
    colors as defaultColors,
} from '../mpanels/variables';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';

const colors = {
    ...defaultColors,
    white: 'rgba(255, 255, 255, 1)',
    darkGray: 'rgba(127, 136, 152, 1)',
    primary: {
        '50': 'rgba(191, 214, 240, 1)',
        '100': 'rgba(154, 185, 219, 1)',
        '200': 'rgba(111, 143, 178, 1)',
        '300': 'rgba(81, 110, 143, 1)',
        '400': 'rgba(46, 70, 98, 1)',
        '500': 'rgba(19, 21, 66, 1)',
        '600': 'rgba(15, 17, 53, 1)',
        '700': 'rgba(11, 13, 40, 1)',
        '800': 'rgba(8, 8, 26, 1)',
        '900': 'rgba(4, 4, 13, 1)',
    },
    additional: {
        '50': 'rgba(244, 251, 251, 1)',
        '100': 'rgba(233, 246, 247, 1)',
        '200': 'rgba(210, 238, 238, 1)',
        '300': 'rgba(188, 229, 230, 1)',
        '400': 'rgba(165, 221, 221, 1)',
        '500': 'rgba(143, 212, 213, 1)',
        '600': 'rgba(122, 188, 189, 1)',
        '700': 'rgba(92, 144, 145, 1)',
        '800': 'rgba(67, 111, 112, 1)',
        '900': 'rgba(41, 77, 78, 1)',
    },
    secondary: {
        '50': 'rgba(237, 251, 254, 1)',
        '100': 'rgba(210, 242, 251, 1)',
        '200': 'rgba(171, 229, 246, 1)',
        '300': 'rgba(83, 200, 235, 1)',
        '400': 'rgba(49, 180, 223, 1)',
        '500': 'rgba(21, 152, 197, 1)',
        '600': 'rgba(20, 121, 166, 1)',
        '700': 'rgba(23, 98, 135, 1)',
        '800': 'rgba(28, 81, 110, 1)',
        '900': 'rgba(28, 68, 93, 1)',
    },
    tertiary: {
        '50': 'rgba(236, 246, 246, 1)',
        '100': 'rgba(217, 237, 237, 1)',
        '200': 'rgba(180, 218, 219, 1)',
        '300': 'rgba(142, 200, 200, 1)',
        '400': 'rgba(105, 181, 182, 1)',
        '500': 'rgba(67, 163, 164, 1)',
        '600': 'rgba(54, 130, 131, 1)',
        '700': 'rgba(40, 98, 98, 1)',
        '800': 'rgba(27, 65, 66, 1)',
        '900': 'rgba(13, 33, 33, 1)',
    },
    neutral: {
        '10': 'rgba(252, 252, 252, 1)',
        '50': 'rgba(222, 222, 222, 1)',
        '100': 'rgba(192, 192, 192, 1)',
        '200': 'rgba(162, 162, 162, 1)',
        '300': 'rgba(132, 132, 132, 1)',
        '400': 'rgba(132, 132, 132, 1)',
        '500': 'rgba(102, 102, 102, 1)',
        '600': 'rgba(82, 82, 82, 1)',
        '700': 'rgba(61, 61, 61, 1)',
        '800': 'rgba(41, 41, 41, 1)',
        '900': 'rgba(20, 20, 20, 1)',
    },
    success: 'rgba(68, 141, 72, 1)',
    info: 'rgba(20, 121, 166, 1)',
    warning: 'rgba(245, 172, 45, 1)',
    error: 'rgba(202, 102, 77, 1)',
};

const palette = {
    ...defaultVariables.colors.palette,
    primary: {
        ...colors.primary,
        main: colors.primary['500'],
        contrastText: colors.white,
    },
    additional: colors.additional,
    secondary: {
        ...colors.secondary,
        main: colors.secondary['500'],
        contrastText: colors.white,
    },
    tertiary: {
        ...colors.tertiary,
        main: colors.tertiary['500'],
        contrastText: colors.white,
    },
    neutral: colors.neutral,
    border: {
        main: 'rgba(247, 244, 242, 1)',
    },
    background: {
        default: colors.neutral['10'],
        menuItemHover: 'rgba(141, 144, 210, 0.1)',
    },
    text: {
        primary: colors.neutral['700'],
        secondary: colors.neutral['500'],
    },
};

const fonts = {
    primary: 'Montserrat, sans-serif',
    secondary: 'Open Sans, sans-serif',
    additional: 'Open Sans, sans-serif',
    body: {
        fontSize: '1rem',
    },
};

type PharmaInsightThemeVariables = ThemeVariables & {
    colors: {
        palette: ThemeVariables['colors']['palette'] & typeof palette;
    };
    boxShadows: ThemeVariables['boxShadows'] & { activeTabShadow: string };
    borderRadius: ThemeVariables['borderRadius'] & {
        tabs: string;
        buttons: string;
        inputs: string;
    };
};

export const variables: PharmaInsightThemeVariables = {
    themeName: 'pharmainsight',
    sortOrder: {
        ...defaultVariables.sortOrder,
        countriesSelectOptions: [[String(countries['de'].phraseId)], ['*']],
    },
    colors: {
        palette,
        links: {
            main: colors.tertiary['500'],
            hover: colors.tertiary['400'],
            active: colors.tertiary['700'],
            disabled: colors.neutral['300'],
        },
        error: {
            main: colors.error,
        },
        other: {
            badge: {
                topMenu: {
                    color: colors.white,
                    background: colors.warning,
                },
            },
            chip: {
                default: {
                    background: addOpacityToThemeColor(palette.primary.main, 0.1),
                    text: palette.primary.main,
                },
                methodologyType: {
                    telephone: {
                        background: 'rgba(34, 66, 181, 0.05)',
                        text: colors.primary['400'],
                    },
                    online: {
                        background: 'rgba(34, 66, 181, 0.05)',
                        text: colors.primary['400'],
                    },
                    inPerson: {
                        background: 'rgba(34, 66, 181, 0.05)',
                        text: colors.primary['400'],
                    },
                },
            },
            table: {
                evenRowHighlight: colors.neutral['10'],
                hoverHighlight: 'transparent',
                headerText: palette.text.primary,
                text: palette.text.primary,
                hoverText: palette.primary['200'],
            },
            background: {
                ...defaultVariables.colors.other.background,
                header: 'rgba(255, 255, 255, 0.92)',
                topMenu: colors.white,
                footer: colors.white,
                highlight: palette.background.menuItemHover,
                highlightHover: palette.background.menuItemHover,
                success: addOpacityToThemeColor(colors.success, 0.08),
                warning: addOpacityToThemeColor(colors.warning, 0.1),
                info: addOpacityToThemeColor(colors.info, 0.05),
                error: addOpacityToThemeColor(colors.error, 0.05),
                divideline: 'rgba(0, 0, 0, 0.16)',
                preloader: palette.primary.main,
            },
            text: {
                ...defaultVariables.colors.other.text,
                header: colors.primary['500'],
                registrationDescription: colors.white,
                footer: colors.neutral['500'],
                copyrightText: colors.neutral['300'],
                success: colors.success,
                warning: colors.warning,
                info: colors.info,
                error: colors.error,
                gray: colors.neutral['500'],
                lightGray: palette.border.main,
            },
            inputs: {
                default: colors.neutral['400'],
                hover: colors.primary['500'],
                focus: colors.primary['500'],
            },
            icon: {
                stroke: {
                    primary: colors.additional['500'],
                    secondary: colors.additional['500'],
                    tertiary: colors.additional['500'],
                },
                background: {
                    primary: 'rgba(143, 212, 213, 0.3)',
                    secondary: 'rgba(143, 212, 213, 0.3)',
                    tertiary: 'rgba(143, 212, 213, 0.3)',
                },
            },
            notifications: {
                success: colors.success,
                warning: colors.warning,
                info: colors.info,
                error: colors.error,
            },
        },
    },
    fonts,
    borderRadius: {
        primary: '8px',
        secondary: '16px',
        tertiary: '32px',
        topMenu: '19px',
        tabs: '24px',
        buttons: '24px',
        inputs: '24px',
        mobileTopMenu: '0 0 18px 18px',
    },
    padding: {
        ...defaultVariables.padding,
        inputsDefault: '8px 16px',
        tableCellPadding: '8px 16px',
        elementPadding: '0 0 12px 0',
    },
    margin: {
        ...defaultVariables.margin,
    },
    boxShadows: {
        popoverShadow: 'none',
        tableShadow: 'none',
        tableSelectedShadow: `inset 0 2px 4px ${addOpacityToThemeColor(colors.black, 0.06)}`,
        headerShadow: 'initial',
        buttonShadow: 'initial',
        inputShadow: '0px 0px 0px 2px rgba(37, 42, 85, 0.50)',
        tooltipShadow: `0px 4px 4px 0px ${addOpacityToThemeColor(palette.text.primary, 0.16)}`,
        descriptiveShadow: 'initial',
        activeTabShadow: `0px 5px 4px -3px ${addOpacityToThemeColor(palette.primary.main, 0.32)}`,
    },
    layout: {
        helpCentreInsteadFaq: true,
        home: {
            introBanner: {
                visible: true,
                linkUrl: 'https://www.pharma-insight.de/',
            },
            hasBackgroundImage: 'fullwidth',
            pageTitle: {
                textAlign: 'left',
                position: 'inline',
            },
            description: {
                visible: true,
                position: 'top',
            },
            picBlock: {
                visible: false,
                position: 'top',
            },
            iconsBlock: {
                visible: true,
                position: 'bottom',
                inlineIcons: false,
                paperBox: false,
            },
            registrationForm: {
                fullwidthForm: false,
                showStepTitle: false,
                stepperOrientation: 'horizontal',
            },
        },
        footer: {
            fullWidth: false,
            logoPosition: 'left',
            themeColorSocialIcons: false,
            companyLinkVisible: false,
            inlineFollowUs: true,
            hasFollowUsTitle: false,
        },
    },
    cookie: {
        button: {
            hover: {
                color: 'white',
                backgroundColor: colors.primary['400'],
            },
            borderRadius: '8px',
            shadow: 'initial',
        },
        toggle: {
            initial: {
                selected: {
                    toggleColor: colors.primary['400'],
                    backgroundColor: addOpacityToThemeColor(colors.primary['400'], 0.5),
                },
                unselected: {
                    toggleColor: colors.white,
                    backgroundColor: colors.neutral['200'],
                },
            },
            disabled: {
                toggleColor: colors.neutral['50'],
                backgroundColor: colors.neutral['200'],
            },
        },
    },
    typography: {
        fontFamily: fonts.primary,
        h1: {
            color: colors.primary['500'],
            fontFamily: fonts.primary,
            fontSize: '2.375rem', // 38px
            fontWeight: '600',
        },
        h2: {
            color: colors.primary['500'],
            fontFamily: fonts.primary,
            fontSize: '2rem', // 32px
            fontWeight: '600',
        },
        h3: {
            color: colors.primary['500'],
            fontFamily: fonts.primary,
            fontSize: '1.5rem', // 24px
            fontWeight: '600',
        },
        h4: {
            color: colors.primary['500'],
            fontFamily: fonts.primary,
            fontSize: '1.125rem', // 18px
            fontWeight: '600',
        },
        h5: {
            color: colors.primary['500'],
            fontFamily: fonts.primary,
            fontSize: '1rem', // 16px
            fontWeight: '600',
        },
        h6: {
            color: colors.primary['500'],
            fontFamily: fonts.primary,
            fontSize: '1rem', // 16px
            fontWeight: '600',
        },
        body1: {
            fontSize: '1rem', // 16px
            fontWeight: '400',
            fontFamily: fonts.secondary,
        },
        body2: {
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        subtitle1: {
            fontSize: '1rem', // 16px
            fontWeight: '400',
            fontFamily: fonts.secondary,
        },
        subtitle2: {
            fontSize: '0.875rem', // 14px
            fontWeight: '400',
            fontFamily: fonts.secondary,
        },
        subtitle3: {
            fontSize: '0.75rem', // 12px
            fontWeight: '400',
            fontFamily: fonts.secondary,
        },
        greyLabel: {
            fontSize: '0.75rem', // 12px
            fontFamily: fonts.secondary,
        },
        link: {
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            },
            '&:active': {
                textDecoration: 'none',
            },
            '&:disabled': {
                textDecoration: 'none',
            },
        },
    },
};
